/**
 * Mapování API read modelu kalendáře (`GetCalendarTermsResponse`) na view model `CalendarEvent`.
 * Normalizuje stav termínu (volný string, nekonzistentní velikost písmen) a typ dopravy série.
 */
import { addDays, format, parse, parseISO } from 'date-fns';
import type { ParamCodec } from '@/shared/navigation/core';
import type {
  CalendarSerieLayersResponse,
  CalendarTermTransport,
  CalendarTermResponse,
  CalendarTransportSchemeResponse,
  GetCalendarTermsResponse,
  LayerState,
  PlanningTransportFilter,
  SerieLayerStateItem,
  SerieTransportType,
} from '@/api/generated/schemas';
import { SerieLayer } from '@/api/generated/schemas';

export type CalendarStatus = 'draft' | 'contracted' | 'confirmed' | 'canceled' | 'unknown';
export type CalendarTransport = 'air' | 'coach' | 'combined';
export type CalendarTransportFilter = 'all' | 'air' | 'coach';

export interface CalendarEvent {
  termId: string;
  legacyTermId: number;
  productSerieId: string;
  productId: string;
  projectId: string;
  name: string;
  /** První den termínu (lokální půlnoc) */
  start: Date;
  /** Poslední den termínu = start + nights (lokální půlnoc, inkluzivně) */
  end: Date;
  nights: number;
  status: CalendarStatus;
  statusRaw: string;
  transport: CalendarTransport;
  capacity: number | null;
  schema: CalendarTransportSchemeResponse | null;
  /** Doprava termínu — příznak vlastní definice a hodnoty obou směrů (stanice, čas, přiřazená trasa). */
  termTransport: CalendarTermTransport;
  /** Stav první vrstvy termínu („Stav termínu"). */
  termLayerState: LayerState;
  /** Stav dopravní vrstvy termínu (Locked/Draft/Approved) – řídí editovatelnost a schvalování dopravy. */
  operationLayerState: LayerState;
  /** Všechny vrstvy termínu (pro panely sidebaru a odvození nabídky schválení). */
  layers: SerieLayerStateItem[];
  /** Vrstvy nadřazeného seriálu – zdroj nabídky „schválit až po X“. */
  serieLayers: SerieLayerStateItem[];
  /** Termín dědí nákladové schéma ze série (false = vlastní override). */
  inheritsProgramScheme: boolean;
  /** Termín používá sdílený tarif série (false = vlastní tarif). */
  inheritsTariff: boolean;
}

/** Vrstvy termínu jako pole (symetrie s `GetTermDetail`). */
function toTermLayers(term: CalendarTermResponse): SerieLayerStateItem[] {
  return [
    { layer: SerieLayer.Terms, state: term.termLayerState, sortOrder: 0 },
    { layer: SerieLayer.OperationScheme, state: term.operationLayerState, sortOrder: 1 },
    { layer: SerieLayer.ProductCostScheme, state: term.productCostLayerState, sortOrder: 2 },
    { layer: SerieLayer.Calculation, state: term.calculationLayerState, sortOrder: 3 },
  ];
}

function toSerieLayers(serie: CalendarSerieLayersResponse): SerieLayerStateItem[] {
  return [
    { layer: SerieLayer.Terms, state: serie.termsLayerState, sortOrder: 0 },
    { layer: SerieLayer.OperationScheme, state: serie.operationSchemeLayerState, sortOrder: 1 },
    { layer: SerieLayer.ProductCostScheme, state: serie.productCostSchemeLayerState, sortOrder: 2 },
    { layer: SerieLayer.Calculation, state: serie.calculationLayerState, sortOrder: 3 },
  ];
}

const STATUS_BY_NORMALIZED: Record<string, CalendarStatus> = {
  draft: 'draft',
  contracted: 'contracted',
  confirmed: 'confirmed',
  canceled: 'canceled',
  cancelled: 'canceled',
};

/** Normalizace volného stringu `aggregatedStatus` (case-insensitive, `unknown` fallback) */
export function normalizeStatus(raw: string): CalendarStatus {
  return STATUS_BY_NORMALIZED[raw.trim().toLowerCase()] ?? 'unknown';
}

export function mapTransport(type: SerieTransportType): CalendarTransport {
  switch (type) {
    case 'Flight':
      return 'air';
    case 'Bus':
      return 'coach';
    default:
      return 'combined';
  }
}

export function matchesTransportFilter(
  transport: CalendarTransport,
  filter: CalendarTransportFilter
): boolean {
  if (filter === 'all') return true;
  return transport === filter || transport === 'combined';
}

/** Filtr dopravy ze stromu (Air/Bus/bez filtru) → filtr kalendáře. */
export function toCalendarTransportFilter(
  transportType: PlanningTransportFilter | null
): CalendarTransportFilter {
  if (transportType === 'Air') return 'air';
  if (transportType === 'Bus') return 'coach';
  return 'all';
}

function toEvent(
  term: CalendarTermResponse,
  schemesById: Map<string, CalendarTransportSchemeResponse>,
  serieLayersById: Map<string, SerieLayerStateItem[]>
): CalendarEvent {
  const start = parseISO(term.dateFrom);
  return {
    termId: term.termId,
    legacyTermId: term.legacyTermId,
    productSerieId: term.productSerieId,
    productId: term.productId,
    projectId: term.projectId,
    name: term.name,
    start,
    end: addDays(start, term.nights),
    nights: term.nights,
    status: normalizeStatus(term.aggregatedStatus),
    statusRaw: term.aggregatedStatus,
    transport: mapTransport(term.serieTransportType),
    capacity: term.capacity ?? null,
    schema: (term.transportSchemeId && schemesById.get(term.transportSchemeId)) || null,
    termTransport: term.transport,
    termLayerState: term.termLayerState,
    operationLayerState: term.operationLayerState,
    layers: toTermLayers(term),
    serieLayers: serieLayersById.get(term.productSerieId) ?? [],
    inheritsProgramScheme: term.inheritsProgramScheme,
    inheritsTariff: term.inheritsTariff,
  };
}

export function buildCalendarEvents(response: GetCalendarTermsResponse): CalendarEvent[] {
  const schemesById = new Map(response.transportSchemes.map((schema) => [schema.schemeId, schema]));
  // Stavy vrstev seriálu chodí normalizované stranou (jedna série = mnoho termínů v okně měsíce).
  const serieLayersById = new Map(
    response.series.map((serie) => [serie.productSerieId, toSerieLayers(serie)])
  );
  return response.terms.map((term) => toEvent(term, schemesById, serieLayersById));
}

/** 'YYYY-MM' → Date (první den měsíce) */
export function parseMonthParam(month: string): Date {
  return parse(month, 'yyyy-MM', new Date());
}

/** Codec navigačního parametru měsíce ve tvaru 'YYYY-MM' (deep-link kalendářového režimu). */
export const monthCodec: ParamCodec<string> = {
  encode: (value) => value,
  decode: (raw) => {
    if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(raw)) {
      throw new Error(`Invalid month: ${raw}`);
    }
    return raw;
  },
};

/** Date → 'YYYY-MM' */
export function formatMonthParam(date: Date): string {
  return format(date, 'yyyy-MM');
}

/** Date → 'YYYY-MM-DD' (lokální, bez časové zóny) */
export function isoDate(date: Date): string {
  return format(date, 'yyyy-MM-dd');
}
